import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, PageContainer, PageHeader, Tabs, useToast } from '../../ui/components';
import { Icon } from '../../ui/icons';
import { useProperty } from '../../app/PropertyContext';
import { useAuth } from '../../auth/AuthContext';
import { useT } from '../../i18n';
import { usersApi } from './usersApi';
import UsersTab from './UsersTab';
import RolesTab from './RolesTab';
import InvitesTab from './InvitesTab';
import UserEditorDrawer from './UserEditorDrawer';
import RoleEditorDrawer from './RoleEditorDrawer';
import InviteDrawer from './InviteDrawer';
import '../settings/settings.css';
import './users.css';

const TABS = [
  { id: 'users', label: 'Users' },
  { id: 'roles', label: 'Roles' },
  { id: 'invites', label: 'Invitations' },
];

export default function UsersPage() {
  const toast = useToast();
  const t = useT();
  const { properties, hasPermission } = useProperty();
  const { user: authUser } = useAuth();
  const [tab, setTab] = useState('users');

  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [invites, setInvites] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);

  const [editingUser, setEditingUser] = useState(null);
  const [editingRole, setEditingRole] = useState(undefined); // undefined=closed, null=new, role=edit
  const [inviteOpen, setInviteOpen] = useState(false);

  const canManageUsers = hasPermission('users.manage');
  const canManageRoles = hasPermission('roles.manage');

  const reloadUsers = useCallback(async () => setUsers(await usersApi.listUsers()), []);
  const reloadRoles = useCallback(async () => setRoles(await usersApi.listRoles()), []);
  const reloadInvites = useCallback(async () => setInvites(await usersApi.listInvites()), []);

  useEffect(() => {
    (async () => {
      try {
        const [u, r, i] = await Promise.all([usersApi.listUsers(), usersApi.listRoles(), usersApi.listInvites()]);
        setUsers(u); setRoles(r); setInvites(i);
        if (canManageRoles) {
          try { setCatalog(await usersApi.permissionCatalog()); } catch { /* no perm */ }
        }
      } catch (err) {
        toast.error(t('Could not load users'), { description: err?.message });
      } finally {
        setLoading(false);
      }
    })();
  }, [toast, canManageRoles, t]);

  const rolesById = useMemo(() => new Map(roles.map((r) => [r.id, r])), [roles]);
  const propsById = useMemo(() => new Map(properties.map((p) => [p.id, p])), [properties]);

  const currentUserId = authUser?.id ?? null;

  return (
    <PageContainer size="full">
      <div className="set-page">
        <PageHeader
          title={t('Users & roles')}
          description={t('Manage who can access the system and what they can do, with per-property roles and approval limits.')}
          icon={<Icon.Users size={22} />}
          tabs={<Tabs tabs={TABS.map((tb) => ({ ...tb, label: t(tb.label) }))} value={tab} onChange={setTab} ariaLabel={t('User management sections')} />}
          actions={
            tab === 'roles'
              ? (canManageRoles && <Button variant="primary" size="sm" leadingIcon={<Icon.Plus size={15} />} onClick={() => setEditingRole(null)}>{t('New role')}</Button>)
              : (canManageUsers && <Button variant="primary" size="sm" leadingIcon={<Icon.Plus size={15} />} onClick={() => setInviteOpen(true)}>{t('Invite user')}</Button>)
          }
        />

        {tab === 'users' && (
          <UsersTab
            users={users}
            loading={loading}
            rolesById={rolesById}
            propsById={propsById}
            canManage={canManageUsers}
            currentUserId={currentUserId}
            onEdit={setEditingUser}
            onChanged={reloadUsers}
          />
        )}

        {tab === 'roles' && (
          <RolesTab
            roles={roles}
            loading={loading}
            canManage={canManageRoles}
            onEdit={setEditingRole}
            onChanged={reloadRoles}
          />
        )}

        {tab === 'invites' && (
          <InvitesTab
            invites={invites}
            loading={loading}
            canManage={canManageUsers}
            onChanged={reloadInvites}
            onInvite={() => setInviteOpen(true)}
          />
        )}

        {editingUser && (
          <UserEditorDrawer
            open={!!editingUser}
            user={editingUser}
            roles={roles}
            properties={properties}
            isSelf={editingUser.id === currentUserId}
            onClose={() => setEditingUser(null)}
            onSaved={async () => { setEditingUser(null); await reloadUsers(); }}
          />
        )}

        {editingRole !== undefined && (
          <RoleEditorDrawer
            open={editingRole !== undefined}
            role={editingRole}
            catalog={catalog}
            onClose={() => setEditingRole(undefined)}
            onSaved={async () => { setEditingRole(undefined); await reloadRoles(); }}
          />
        )}

        <InviteDrawer
          open={inviteOpen}
          roles={roles}
          properties={properties}
          onClose={() => setInviteOpen(false)}
          onSent={async () => { await reloadInvites(); }}
        />
      </div>
    </PageContainer>
  );
}
